import React, { useState, useEffect, useRef } from "react";
import "./Instruction.css";
import { useSelector } from "react-redux";
import { Document, Page, pdfjs } from "react-pdf";
import {
  IonContent,
  IonHeader,
  IonPage,
  IonTitle,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  IonFooter,
  IonSlides,
  IonSlide,
} from "@ionic/react";
import {
  arrowBackOutline,
  chevronBackOutline,
  chevronForwardOutline,
  openOutline,
} from "ionicons/icons";
import WrappedSpinner from "../components/WrappedSpinner";
import Error from "../components/Error";
import { JSONInterface } from "../interfaces/JSONInterface";
import { StateInterface } from "../interfaces/StateInterface";
import { ComponentProps } from "../interfaces/ComponentProps";
import { isNumber } from "util";
import { ErrorRefs } from "../ErrorRefs";
import { Constants } from "../Constants";

//source of below line: https://github.com/wojtekmaj/react-pdf#standard-browserify-and-others
//pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;

const slideOpts = {
  initialSlide: 0,
  speed: 200,
  zoom: { maxRatio: 2 },
  spaceBetween: 50,
};

const Instruction: React.FC<
  ComponentProps & { fetchProjectsJSON: Function }
> = ({ match, fetchProjectsJSON }) => {
  const [pageNumbers, setPageNumbers] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [pageDimensions, setPageDimensions] = useState<{
    width: number;
    height: number;
  }>({ width: 0, height: 0 });
  const [pageScale, setPageScale] = useState<number>(1);
  const [documentRef, setDocumentRef] = useState<HTMLDivElement | null>(null);
  const [swiper, setSwiper] = useState<any>(null);
  const [documentError, setDocumentError] = useState<string | null>(null);
  const slideRef = useRef<HTMLIonSlidesElement>(null);
  const json: JSONInterface | null = useSelector(
    (state: StateInterface) => state.data.json
  );
  const loaded: boolean = useSelector(
    (state: StateInterface) => state.data.loaded
  );
  const error: string | null = useSelector(
    (state: StateInterface) => state.data.error
  );

  const onDocumentLoadSuccess = ({ numPages }: pdfjs.PDFDocumentProxy) => {
    setPageNumbers(Array.from(Array(numPages), (_, index) => index + 1));
    slideRef.current
      ?.getActiveIndex()
      .then((index) => setCurrentPage(index + 1));
  };

  const onPageLoadSuccess = (page: pdfjs.PDFPageProxy) => {
    const { width, height } = page.getViewport({ scale: 1 });
    if (!pageDimensions.width || !pageDimensions.height) {
      setPageDimensions({ width: width, height: height });
      const elementDimensions = getElementDimensions(documentRef);
      setPageScale(
        Math.min(
          elementDimensions.width / width,
          elementDimensions.height / height
        ) * Constants.PAGE_SCALING_FACTOR
      );
    }
  };

  const getElementDimensions = (element: HTMLDivElement | null) => {
    const elementDimensions = {
      width: 0,
      height: 0,
    };
    if (element) {
      elementDimensions.width = parseFloat(
        window.getComputedStyle(element).width
      );
      elementDimensions.height = parseFloat(
        window.getComputedStyle(element).height
      );
    }
    return elementDimensions;
  };

  useEffect(() => {
    const handleResize = () => {
      const elementDimensions = getElementDimensions(documentRef);
      setPageScale(
        Math.min(
          elementDimensions.width / pageDimensions.width,
          elementDimensions.height / pageDimensions.height
        ) * Constants.PAGE_SCALING_FACTOR
      );
      swiper?.zoom.out();
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [documentRef, pageDimensions, swiper]);

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton
              routerLink={`/projects/${match.params.tier}`}
              routerDirection="back"
            >
              <IonIcon md={arrowBackOutline} ios={chevronBackOutline}></IonIcon>
            </IonButton>
          </IonButtons>
          <IonTitle>{match.params.title}</IonTitle>
          <IonButtons slot="end">
            <IonButton
              href={
                json?.projects.find(
                  (project) => project.title === match.params.title
                )?.instructionsUrl
              }
              target="_blank"
            >
              <IonIcon icon={openOutline}></IonIcon>
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {error ? (
          <Error error={error} onButtonClick={fetchProjectsJSON} />
        ) : loaded ? (
          <Document
            className="instruction-document"
            inputRef={(ref) => {
              setDocumentRef(ref);
            }}
            file={
              json?.projects.find(
                (project) => project.title === match.params.title
              )?.instructionsUrl
            }
            loading={<WrappedSpinner />}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={() =>
              setDocumentError(ErrorRefs["Failed to load PDF file"])
            }
            error={
              <Error
                error={documentError}
                onButtonClick={() => window.location.reload(false)}
              />
            }
          >
            <IonSlides
              className="instruction-slides"
              options={slideOpts}
              ref={slideRef}
              onIonSlidesDidLoad={() => {
                slideRef.current
                  ?.getSwiper()
                  .then((swiper) => setSwiper(swiper));
              }}
              onIonSlideDidChange={() => {
                slideRef.current
                  ?.getActiveIndex()
                  .then(
                    (index) => isNumber(index) && setCurrentPage(index + 1)
                  );
              }}
            >
              {pageNumbers.map((pageNumber) => (
                <IonSlide key={pageNumber}>
                  <Page
                    className="swiper-zoom-container"
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                    scale={pageScale}
                    pageNumber={pageNumber}
                    onLoadSuccess={onPageLoadSuccess}
                  />
                </IonSlide>
              ))}
            </IonSlides>
          </Document>
        ) : (
          <WrappedSpinner />
        )}
      </IonContent>
      <IonFooter className="ion-no-border">
        <div className="page-navigator">
          <IonButton
            className="page-navigator-button"
            fill="clear"
            color="medium"
            onClick={() => slideRef.current?.slidePrev()}
            disabled={!currentPage || !pageNumbers.length || currentPage === 1}
          >
            <IonIcon icon={chevronBackOutline} />
          </IonButton>
          <h6 className="page-navigator-text">
            {currentPage ? currentPage : "-"} /
            {pageNumbers.length ? pageNumbers.length : "-"}
          </h6>
          <IonButton
            className="page-navigator-button"
            fill="clear"
            color="medium"
            onClick={() => slideRef.current?.slideNext()}
            disabled={
              !currentPage ||
              !pageNumbers.length ||
              currentPage === pageNumbers.length
            }
          >
            <IonIcon icon={chevronForwardOutline} />
          </IonButton>
        </div>
      </IonFooter>
    </IonPage>
  );
};

export default Instruction;
